// src/services/casePrepBuddyExposureReviewService.js
// Provider-facing service for the Case Prep Buddy exposure-review branch.
// Uses the PathTracer web-app API base URL from axios.defaults.baseURL.
// Do not put Arkham API keys, worker Client-ID, or worker Client-Secret values in the browser.

import axios from 'axios';

const ENDPOINTS = {
  entitySearch: 'arkham/arkham_entity_search.php',
  exposureReview: 'arkham/arkham_exposure_review.php',
  exposureTransfers: 'arkham/arkham_exposure_transfers.php'
};

function getPathTracerAuthHeaders() {
  const headers = {
    'Content-Type': 'application/json'
  };

  const token = typeof window !== 'undefined'
    ? window.localStorage.getItem('token')
    : '';

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function exposureDebugEnabled() {
  if (typeof window === 'undefined') return false;
  return window.CASE_PREP_ARKHAM_DEBUG === true || window.localStorage.getItem('casePrepArkhamDebug') === '1';
}

function withDebugFlag(payload) {
  if (!exposureDebugEnabled()) return payload || {};
  return {
    ...(payload || {}),
    debug: true
  };
}

async function postJson(url, payload) {
  const body = withDebugFlag(payload);
  const debug = exposureDebugEnabled();

  if (debug) {
    // eslint-disable-next-line no-console
    console.info('[CasePrep Arkham] request', url, body);
  }

  try {
    const response = await axios.post(url, body, {
      headers: getPathTracerAuthHeaders()
    });

    if (debug) {
      // eslint-disable-next-line no-console
      console.info('[CasePrep Arkham] response', url, response.data);
    }

    return response.data || { status: 'ok' };
  } catch (error) {
    const data = error?.response?.data;

    if (debug) {
      // eslint-disable-next-line no-console
      console.error('[CasePrep Arkham] error', url, data || error);
    }

    if (data?.allowContinue) {
      return data;
    }

    const message =
      data?.message ||
      data?.error ||
      error?.message ||
      'Request failed.';

    throw new Error(message);
  }
}

function splitCsvText(value) {
  return String(value || '')
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function normalizeExposureFilters(formFilters = {}) {
  const filters = {
    chains: Array.isArray(formFilters.chains)
      ? formFilters.chains.filter(Boolean)
      : splitCsvText(formFilters.chainsText || formFilters.chains),
    tokens: Array.isArray(formFilters.tokens)
      ? formFilters.tokens.filter(Boolean)
      : splitCsvText(formFilters.tokensText || formFilters.tokens),
    tags: Array.isArray(formFilters.tags)
      ? formFilters.tags.filter(Boolean)
      : splitCsvText(formFilters.tagsText || formFilters.tags),
    usdGte: String(formFilters.usdGte || '').trim(),
    limit: Number(formFilters.limit || 25)
  };

  const timeMode = formFilters.timeMode || 'last';
  if (timeMode === 'absolute') {
    filters.timeGte = String(formFilters.timeGte || formFilters.dateFrom || '').trim();
    filters.timeLte = String(formFilters.timeLte || formFilters.dateTo || '').trim();
    filters.timeLast = '';
  } else {
    filters.timeLast = String(formFilters.timeLast || '30d').trim();
    filters.timeGte = '';
    filters.timeLte = '';
  }

  filters.limit = Number.isFinite(filters.limit) ? Math.max(1, Math.min(100, filters.limit)) : 25;

  return filters;
}

export function searchExposureTargets({ query, limit = 8, forceRefresh = false } = {}) {
  return postJson(ENDPOINTS.entitySearch, {
    query,
    limit,
    forceRefresh
  });
}

export function lookupExposureReview({ targetKind, address, entityId, filters, forceRefresh = false } = {}) {
  return postJson(ENDPOINTS.exposureReview, {
    targetKind,
    address,
    entityId,
    filters,
    forceRefresh
  });
}

export function lookupExposureTransfers({ target, counterparty, flow, filters, offset = 0, limit = 25, forceRefresh = false } = {}) {
  return postJson(ENDPOINTS.exposureTransfers, {
    target,
    counterparty,
    flow,
    filters: {
      ...(filters || {}),
      limit
    },
    offset,
    forceRefresh
  });
}
